import { useEffect } from "react";
import { ProductProposal } from "@/types/trackLabeling";
import {
  getGridNavigationTargetIndex,
  getNumericHotkeyTargetIndex,
} from "./keyboard";
import { isEditableTarget } from "./utils";

type UseTrackKeyboardShortcutsArgs = {
  canConfirmSelectedProduct: boolean;
  fastScrollStep: number;
  frameProducts: ProductProposal[];
  hasReachedEnd: boolean;
  /** The labeler said the bounding box jumped, and picks the frame to end the track at. */
  isChoosingEndFrame: boolean;
  /** When a modal is open, global shortcuts are suppressed. */
  isModalOpen: boolean;
  /** While an answer is being sent or the task released, global shortcuts are suppressed. */
  isLocked: boolean;
  isTrackingConfirmed: boolean;
  onBadDetection: () => void;
  onCancelChoosingEndFrame: () => void;
  onConfirmAssociation: () => void;
  onNext: (step?: number) => void;
  onPrevious: (step?: number) => void;
  onPrimaryConfirm: () => void;
  onProductNotListed: () => void;
  onSelectProduct: (productId: string) => void;
  selectedProduct: string;
};

export function useTrackKeyboardShortcuts({
  canConfirmSelectedProduct,
  fastScrollStep,
  frameProducts,
  hasReachedEnd,
  isChoosingEndFrame,
  isModalOpen,
  isLocked,
  isTrackingConfirmed,
  onBadDetection,
  onCancelChoosingEndFrame,
  onConfirmAssociation,
  onNext,
  onPrevious,
  onPrimaryConfirm,
  onProductNotListed,
  onSelectProduct,
  selectedProduct,
}: UseTrackKeyboardShortcutsArgs) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isModalOpen || isLocked || isEditableTarget(event.target)) {
        return;
      }

      // Holding a key will trigger the repeat events. We only allow arrows keys to repeat (to
      // scroll frames or move through the grid).
      if (event.repeat && !event.key.startsWith("Arrow")) {
        return;
      }

      const lowerKey = event.key.toLowerCase();

      if (!isTrackingConfirmed) {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          onNext(event.repeat ? fastScrollStep : 1);
          return;
        }

        if (event.key === "ArrowLeft") {
          event.preventDefault();
          onPrevious(event.repeat ? fastScrollStep : 1);
          return;
        }

        if (event.key === " ") {
          event.preventDefault();
          return;
        }

        // While choosing the end frame, there are no other keys. Ending the track needs a click on
        // its button. Enter (the habit for Yes) does nothing, and its default is prevented so it
        // can't press a focused button, such as End.
        if (isChoosingEndFrame) {
          if (event.key === "Enter") {
            event.preventDefault();
          } else if (event.key === "Escape") {
            event.preventDefault();
            onCancelChoosingEndFrame();
          }
          return;
        }

        // 1 (Skip) does nothing until the last frame has been reached. The Skip button is hidden
        // until then.
        if (lowerKey === "1" && hasReachedEnd) {
          event.preventDefault();
          onBadDetection();
          return;
        }

        if (event.key === "Enter") {
          event.preventDefault();
          if (hasReachedEnd) {
            onPrimaryConfirm();
          }
          return;
        }
        return;
      }

      const currentIndex = frameProducts.findIndex(
        (product) => product.product_id === selectedProduct,
      );

      if (
        event.key === "ArrowRight" ||
        event.key === "ArrowLeft" ||
        event.key === "ArrowDown" ||
        event.key === "ArrowUp"
      ) {
        event.preventDefault();

        if (currentIndex >= 0 && frameProducts.length > 0) {
          const nextIndex = getGridNavigationTargetIndex(
            currentIndex,
            event.key,
            frameProducts.length,
          );

          if (nextIndex >= 0 && frameProducts[nextIndex]) {
            onSelectProduct(frameProducts[nextIndex].product_id);
          }
        }
        return;
      }

      const hotkeyIndex = getNumericHotkeyTargetIndex(
        lowerKey,
        frameProducts.length,
      );
      if (hotkeyIndex !== null && frameProducts[hotkeyIndex]) {
        event.preventDefault();
        onSelectProduct(frameProducts[hotkeyIndex].product_id);
        return;
      }

      if (lowerKey === "s") {
        event.preventDefault();
        onProductNotListed();
        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (canConfirmSelectedProduct) {
          onConfirmAssociation();
        }
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    canConfirmSelectedProduct,
    fastScrollStep,
    frameProducts,
    hasReachedEnd,
    isChoosingEndFrame,
    isModalOpen,
    isLocked,
    isTrackingConfirmed,
    onBadDetection,
    onCancelChoosingEndFrame,
    onConfirmAssociation,
    onNext,
    onPrevious,
    onPrimaryConfirm,
    onProductNotListed,
    onSelectProduct,
    selectedProduct,
  ]);
}
